import React, { useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';

const LEAD_SOURCES = ['Referral', 'Google', 'Website', 'Social Media', 'Property Manager', 'Door Knock', 'Trade Show', 'Repeat Customer', 'Other'];

const INITIAL_FORM = {
  name: '', email: '', phone: '', address: '',
  customer_type: 'residential', status: 'prospect', lead_source: '', notes: ''
};

const CustomersTab = ({ customers, onUpdate }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleSubmit = async () => {
    try {
      if (editCustomer) {
        await api.admin.updateCustomer(editCustomer.id, formData);
        toast.success('Customer updated');
      } else {
        await api.admin.createCustomer(formData);
        toast.success('Customer added');
      }
      setIsDialogOpen(false);
      setEditCustomer(null);
      setFormData(INITIAL_FORM);
      onUpdate();
    } catch (error) {
      toast.error('Operation failed');
    }
  };

  const openEdit = (customer) => {
    setEditCustomer(customer);
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      customer_type: customer.customer_type || 'residential',
      status: customer.status || 'prospect',
      lead_source: customer.lead_source || '',
      notes: customer.notes || ''
    });
    setIsDialogOpen(true);
  };

  const openNew = () => {
    setEditCustomer(null);
    setFormData(INITIAL_FORM);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await api.admin.deleteCustomer(id);
      toast.success('Customer deleted');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (c.phone || '').includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-4" data-testid="customers-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-primary-dark border-white/20 text-white w-full sm:w-64"
            data-testid="customer-search"
          />
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-36 bg-primary-dark border-white/20 text-white" data-testid="customer-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="prospect">Prospect</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} className="bg-electric-teal text-primary-dark font-bold" data-testid="add-customer-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Customer Table */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4">Name</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4 hidden md:table-cell">Contact</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4">Type</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4">Status</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4">Lead Source</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4 hidden lg:table-cell">Bookings</th>
                <th className="text-left text-white/60 text-xs md:text-sm font-medium p-3 md:p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.id}>
                  <td className="p-3 md:p-4">
                    <p className="text-white font-medium text-sm">{customer.name}</p>
                    <p className="text-white/50 text-xs md:hidden">{customer.email}</p>
                  </td>
                  <td className="p-3 md:p-4 hidden md:table-cell">
                    <p className="text-white/80 text-sm">{customer.email}</p>
                    <p className="text-white/50 text-xs">{customer.phone}</p>
                  </td>
                  <td className="p-3 md:p-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                      customer.customer_type === 'commercial' 
                        ? 'bg-blue-500/20 text-blue-400' 
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {(customer.customer_type || 'residential').slice(0, 3)}
                    </span>
                  </td>
                  <td className="p-3 md:p-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                      customer.status === 'active' ? 'bg-electric-teal/20 text-electric-teal' :
                      customer.status === 'inactive' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {customer.status || 'prospect'}
                    </span>
                  </td>
                  <td className="p-3 md:p-4 text-white/60 text-xs md:text-sm">{customer.lead_source || '-'}</td>
                  <td className="p-3 md:p-4 text-white/80 text-sm hidden lg:table-cell">{customer.bookings_count || 0}</td>
                  <td className="p-3 md:p-4">
                    <div className="flex gap-1">
                      <Button
                        onClick={() => openEdit(customer)}
                        variant="ghost"
                        size="sm"
                        className="text-electric-teal hover:text-electric-teal/80"
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(customer.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-400"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredCustomers.length === 0 && (
          <p className="text-white/30 text-center py-8">No customers found</p>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-electric-teal">
              {editCustomer ? 'Edit Customer' : 'Add Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/60">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                placeholder="Customer name"
                data-testid="customer-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-primary-dark border-white/20 text-white mt-1"
                  placeholder="email@example.com"
                  data-testid="customer-email-input"
                />
              </div>
              <div>
                <Label className="text-white/60">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-primary-dark border-white/20 text-white mt-1"
                  placeholder="250-555-0000"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/60">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                placeholder="Full address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60">Customer Type</Label>
                <Select value={formData.customer_type} onValueChange={(v) => setFormData({ ...formData, customer_type: v })}>
                  <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/60">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white/60">Lead Source (How did they find you?)</Label>
              <Select value={formData.lead_source} onValueChange={(v) => setFormData({ ...formData, lead_source: v })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                  <SelectValue placeholder="Select source..." />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(src => (
                    <SelectItem key={src} value={src}>{src}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                rows={3}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-white/20 text-white/60">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-electric-teal text-primary-dark font-bold" data-testid="submit-customer-btn">
              {editCustomer ? 'Update' : 'Add'} Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersTab;
